import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface AuthUser {
  id?: string;
  email: string;
  name: string;
  picture?: string;
  access_token?: string;
  user_metadata?: {
    full_name?: string;
    avatar_url?: string;
    bio?: string;
    location?: string;
  };
}

interface LoginCredentials {
  email: string;
  password: string;
  name?: string;
}

interface SignUpMetadata {
  full_name?: string;
  avatar_url?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  signUp: (email: string, password: string, metadata?: SignUpMetadata) => Promise<{ data: any; error: any }>;
  resendVerificationEmail: (email: string) => Promise<void>;
  signInWithGoogle: (redirectUrl?: string) => Promise<void>;
  signInWithFacebook: (redirectUrl?: string) => Promise<void>;
  logout: () => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  refreshSession: () => Promise<boolean>;
  validateSession: () => Promise<boolean>;
}

const DEFAULT_AVATAR_URL = 'https://vhaizqhkodqyqplpqcss.supabase.co/storage/v1/object/public/avatars/default-avatar.png';

// Helper function to check if user data has actually changed
const isUserDataEqual = (user1: AuthUser | null, user2: AuthUser | null): boolean => {
  if (user1 === user2) return true;
  if (!user1 || !user2) return user1 === user2;

  // Compare all relevant fields
  return (
    user1.id === user2.id &&
    user1.email === user2.email &&
    user1.name === user2.name &&
    user1.picture === user2.picture &&
    user1.access_token === user2.access_token &&
    user1.user_metadata?.full_name === user2.user_metadata?.full_name &&
    user1.user_metadata?.avatar_url === user2.user_metadata?.avatar_url &&
    user1.user_metadata?.bio === user2.user_metadata?.bio &&
    user1.user_metadata?.location === user2.user_metadata?.location
  );
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check for existing Supabase session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        console.error('Error getting session:', error);
        
        // Handle specific error types
        if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
          console.error('406 error in AuthContext - authentication/header issue:', error);
          // Show user-friendly error for persistent 406 errors
          toast.error('Authentication service temporarily unavailable. Please try refreshing the page.');
        } else if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
          console.warn('Unauthorized session, setting as unauthenticated');
        } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
          console.error('Network error during authentication:', error);
          toast.error('Network connection issue. Please check your internet connection.');
        } else {
          console.error('Unexpected session error:', error);
        }
        
        // Set as unauthenticated regardless of error type
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
        return;
      }

      if (session) {
        const userData: AuthUser = {
          id: session.user.id,
          email: session.user.email || '',
          name: session.user.user_metadata.full_name || session.user.email?.split('@')[0] || '',
          picture: session.user.user_metadata.avatar_url || DEFAULT_AVATAR_URL,
          access_token: session.access_token,
          user_metadata: {
            full_name: session.user.user_metadata.full_name,
            avatar_url: session.user.user_metadata.avatar_url,
            bio: session.user.user_metadata.bio,
            location: session.user.user_metadata.location,
          }
        };
        // Initial session: just set directly (no previous user to compare)
        setUser(userData);
        setIsAuthenticated(true);
        setIsLoading(false);
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    }).catch((error) => {
      console.error('Unexpected error in getSession:', error);
      
      // Handle different error types
      if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
        console.error('406 error in AuthContext catch - authentication/header issue:', error);
      }
      
      // Set as unauthenticated on any error
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);
    });

    // Check for email confirmation parameters in URL
    const handleEmailConfirmation = async () => {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const urlParams = new URLSearchParams(window.location.search);
      
      // Check for email confirmation tokens in hash or query params
      const accessToken = hashParams.get('access_token') || urlParams.get('access_token');
      const refreshToken = hashParams.get('refresh_token') || urlParams.get('refresh_token');
      const type = hashParams.get('type') || urlParams.get('type');
      
      if (accessToken && refreshToken && type === 'email_confirmation') {
        try {
          console.log('Processing email confirmation...');
          
          // Set the session from the confirmation tokens
          const { data: { session }, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            console.error('Error setting session from email confirmation:', error);
            return;
          }

          if (session) {
            console.log('✅ Email confirmation successful, user authenticated');
            
            // Clear the tokens from URL to prevent reprocessing
            const newURL = window.location.pathname;
            window.history.replaceState(null, '', newURL);
            
            // Store confirmation state for welcome flow
            sessionStorage.setItem('email-just-confirmed', 'true');
            sessionStorage.setItem('confirmed-email', session.user.email || '');
            
            // Show immediate confirmation success
            toast.success('Email Verified Successfully! 🎉', {
              description: 'Your account is now active. Preparing your welcome...',
              duration: 4000
            });
            
            // The onAuthStateChange listener will handle the UI updates
            // Dispatch enhanced welcome event after UI is ready
            setTimeout(() => {
              if (typeof window !== 'undefined' && window.dispatchEvent) {
                window.dispatchEvent(new CustomEvent('email-confirmed', {
                  detail: { 
                    email: session.user.email,
                    isNewUser: true,
                    timestamp: new Date().toISOString()
                  }
                }));
              }
            }, 1000);
          }
        } catch (error) {
          console.error('Error processing email confirmation:', error);
        }
      }
    };

    // Check for email confirmation after initial session check
    handleEmailConfirmation();

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      try {
        console.log('Auth state changed:', event, session?.user?.email);

        if (session) {
          const userData: AuthUser = {
            id: session.user.id,
            email: session.user.email || '',
            name: session.user.user_metadata.full_name || session.user.email?.split('@')[0] || '',
            picture: session.user.user_metadata.avatar_url || DEFAULT_AVATAR_URL,
            access_token: session.access_token,
            user_metadata: {
              full_name: session.user.user_metadata.full_name,
              avatar_url: session.user.user_metadata.avatar_url,
              bio: session.user.user_metadata.bio,
              location: session.user.user_metadata.location,
            }
          };

          // Only update state if user data has actually changed
          // This prevents unnecessary re-renders on token refresh events
          setUser(prevUser => {
            if (isUserDataEqual(prevUser, userData)) {
              console.log('User data unchanged, skipping state update');
              return prevUser; // Return previous reference to prevent re-render
            }
            console.log('User data changed, updating state');
            return userData;
          });
          setIsAuthenticated(true);
          setIsLoading(false);
          
          // For OAuth users, check if profile exists and create if needed (fallback)
          if (event === 'SIGNED_IN' && session.user.app_metadata?.provider !== 'email') {
            try {
              await createUserProfileFallback(session.user.id, session.user.email || '', {
                full_name: session.user.user_metadata.full_name,
                avatar_url: session.user.user_metadata.avatar_url
              });
            } catch (profileError) {
              console.warn('⚠️ OAuth profile fallback creation failed:', profileError);
              // Don't fail the sign-in process
            }
          }
          
          // If this is from email confirmation, show success message
          if (event === 'SIGNED_IN' && window.location.hash.includes('type=email_confirmation')) {
            setTimeout(() => {
              console.log('Email confirmed and signed in successfully');
            }, 500);
          }
        } else {
          setUser(null);
          setIsAuthenticated(false);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error in onAuthStateChange:', error);
        
        // Handle different error types
        if (error instanceof Error) {
          if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
            console.error('406 error in onAuthStateChange - authentication/header issue:', error);
          }
        }
        
        // Set as unauthenticated on any error
        setUser(null);
        setIsAuthenticated(false);
        setIsLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Add effect to handle auth-required events
  useEffect(() => {
    const handleAuthRequired = (event: CustomEvent) => {
      if (!isAuthenticated) {
        // Store the action and data to be executed after auth
        localStorage.setItem('auth_pending_action', JSON.stringify(event.detail));
      }
    };

    window.addEventListener('auth-required', handleAuthRequired as EventListener);
    return () => window.removeEventListener('auth-required', handleAuthRequired as EventListener);
  }, [isAuthenticated]);

  // Add effect to handle pending actions after authentication
  useEffect(() => {
    if (isAuthenticated) {
      const pendingAction = localStorage.getItem('auth_pending_action');
      if (pendingAction) {
        const { action, ...data } = JSON.parse(pendingAction);
        // Execute the pending action
        if (action === 'favorite') {
          // Dispatch an event to trigger the favorite action
          window.dispatchEvent(new CustomEvent('execute-favorite', { detail: data }));
        }
        localStorage.removeItem('auth_pending_action');
      }

      // Check for auth redirect URL (e.g., from SellerDashboard)
      const authRedirect = sessionStorage.getItem('auth_redirect');
      if (authRedirect) {
        sessionStorage.removeItem('auth_redirect');
        // Use setTimeout to ensure navigation happens after auth state is fully settled
        setTimeout(() => {
          window.location.href = authRedirect;
        }, 100);
      }
    }
  }, [isAuthenticated]);

  // Listen for email confirmation success events
  useEffect(() => {
    const handleEmailConfirmed = (event: CustomEvent) => {
      const { email } = event.detail;
      setTimeout(() => {
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('show-toast', {
            detail: {
              type: 'success',
              title: 'Email Confirmed!',
              description: `Welcome! Your email ${email} has been verified and you're now signed in.`
            }
          }));
        }
      }, 1000);
    };

    window.addEventListener('email-confirmed', handleEmailConfirmed as EventListener);
    return () => window.removeEventListener('email-confirmed', handleEmailConfirmed as EventListener);
  }, []);

  const login = async (credentials: LoginCredentials) => {
    try {
      console.log('🔐 Starting login process for:', credentials.email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
      });

      if (error) {
        // Create enhanced error object for email confirmation errors
        if (error.message?.toLowerCase().includes('email not confirmed') || 
            error.message?.toLowerCase().includes('email_not_confirmed')) {
          const enhancedError = new Error(error.message);
          (enhancedError as any).isEmailNotConfirmed = true;
          (enhancedError as any).originalError = error;
          throw enhancedError;
        }
        throw error;
      }

      if (data.user) {
        console.log('✅ Auth login successful for:', data.user.email);
        
        const userData: AuthUser = {
          id: data.user.id,
          email: data.user.email || '',
          name: data.user.user_metadata.full_name || data.user.email?.split('@')[0] || '',
          picture: data.user.user_metadata.avatar_url || DEFAULT_AVATAR_URL,
          access_token: data.session?.access_token,
          user_metadata: {
            full_name: data.user.user_metadata.full_name,
            avatar_url: data.user.user_metadata.avatar_url,
            bio: data.user.user_metadata.bio,
            location: data.user.user_metadata.location,
          }
        };
        setUser(userData);
        setIsAuthenticated(true);
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      throw error;
    }
  };

  // Fallback profile creation for OAuth users or edge cases
  const createUserProfileFallback = async (userId: string, email: string, metadata?: SignUpMetadata) => {
    try {
      console.log('🔄 Creating fallback user profile for:', { userId, email });
      
      // Check if profile already exists
      const { data: existingProfile } = await supabase
        .from('user_profiles')
        .select('id')
        .eq('user_id', userId)
        .single();

      if (existingProfile) {
        console.log('✅ Profile already exists, skipping creation');
        return { data: existingProfile, error: null };
      }
      
      // Generate a unique username from email
      const username = email.split('@')[0].toLowerCase().replace(/[^a-z0-9]/g, '');
      
      const { data, error } = await supabase
        .from('user_profiles')
        .insert({
          user_id: userId,
          username,
          first_name: metadata?.full_name || email.split('@')[0],
          full_name: metadata?.full_name || email.split('@')[0],
          display_name: metadata?.full_name || email.split('@')[0], // Sync display_name with full_name
          avatar_url: metadata?.avatar_url || DEFAULT_AVATAR_URL,
        })
        .select()
        .single();

      if (error) {
        console.error('❌ Failed to create fallback user profile:', error);
        throw error;
      }

      console.log('✅ Fallback user profile created successfully:', data);
      return { data, error: null };
    } catch (error) {
      console.error('❌ Error creating fallback user profile:', error);
      return { data: null, error };
    }
  };

  const signUp = async (email: string, password: string, metadata?: SignUpMetadata) => {
    try {
      console.log('🚀 Starting signup process for:', email);
      
      // Try Supabase standard signup first
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: metadata?.full_name || email.split('@')[0],
            avatar_url: metadata?.avatar_url || DEFAULT_AVATAR_URL,
          }
        }
      });

      if (error) {
        console.error('❌ Supabase signup error:', error);
        throw error;
      }

      // Check if email confirmation is required
      if (data.user && !data.user.email_confirmed_at) {
        console.log('📧 Email confirmation required - user needs to verify email');
        return { 
          data: { 
            ...data, 
            requiresEmailConfirmation: true,
            message: 'Please check your email and click the confirmation link to complete your signup.'
          }, 
          error: null 
        };
      }

      console.log('✅ Signup successful - user is confirmed:', data);
      return { data, error: null };

    } catch (error) {
      console.error('❌ Signup process failed:', error);
      throw error;
    }
  };

  const resendVerificationEmail = async (email: string) => {
    try {
      console.log('📧 Resending verification email via Supabase to:', email);
      
      // Use Supabase standard resend method
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email
      });

      if (error) {
        console.error('❌ Failed to resend verification email:', error);
        throw error;
      }

      console.log('✅ Verification email sent successfully via Supabase');
      return { error: null };
    } catch (error) {
      console.error('❌ Failed to resend verification email:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to send verification email';
      return { error: { message: errorMessage } };
    }
  };

  const signInWithGoogle = async (redirectUrl?: string) => {
    try {
      // Determine a safe redirect target. If none provided, fall back to current location.
      const currentPath = typeof window !== 'undefined' 
        ? `${window.location.pathname}${window.location.search}` 
        : '/';
      const redirectTarget = redirectUrl || currentPath;

      // Persist intended redirect so we can restore even if query params are lost during OAuth
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('auth_redirect', redirectTarget);
      }

      const callbackUrl = `${import.meta.env.VITE_APP_URL}/auth/callback?redirect=${encodeURIComponent(redirectTarget)}`;
        
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: callbackUrl,
        },
      });

      if (error) throw error;
    } catch (error) {
      console.error('Google sign in error:', error);
      throw error;
    }
  };

  const signInWithFacebook = async (redirectUrl?: string) => {
    try {
      // Determine a safe redirect target. If none provided, fall back to current location.
      const currentPath = typeof window !== 'undefined' 
        ? `${window.location.pathname}${window.location.search}` 
        : '/';
      const redirectTarget = redirectUrl || currentPath;

      // Persist intended redirect so we can restore even if query params are lost during OAuth
      if (typeof window !== 'undefined') {
        sessionStorage.setItem('auth_redirect', redirectTarget);
      }

      const callbackUrl = `${import.meta.env.VITE_APP_URL}/auth/callback?redirect=${encodeURIComponent(redirectTarget)}`;
        
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: callbackUrl,
          scopes: 'email',
        },
      });

      if (error) throw error;
    } catch (error) {
      console.error('Facebook sign in error:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      setUser(null);
      setIsAuthenticated(false);
    } catch (error) {
      console.error('Logout error:', error);
      throw error;
    }
  };

  const getAccessToken = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error) {
        console.error('Error getting access token:', error);
        
        if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
          console.error('406 error in getAccessToken - authentication/header issue:', error);
        }
        
        return null;
      }
      
      return session?.access_token || null;
    } catch (error) {
      console.error('Unexpected error in getAccessToken:', error);
      return null;
    }
  };

  const refreshSession = async () => {
    try {
      // Try to refresh the session with retry logic
      let attempts = 0;
      const maxAttempts = 3;
      let lastError;

      while (attempts < maxAttempts) {
        try {
          const { data: { session }, error } = await supabase.auth.getSession();
          
          if (error) {
            lastError = error;
            console.error(`Session refresh attempt ${attempts + 1} failed:`, error);
            
            if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
              console.error('406 error in refreshSession - authentication/header issue:', error);
            }
            
            // If it's a network error, wait and retry
            if (error.message?.includes('network') || error.message?.includes('fetch') || error.message?.includes('ERR_NETWORK_CHANGED')) {
              attempts++;
              if (attempts < maxAttempts) {
                console.log(`Retrying session refresh in 1 second... (attempt ${attempts}/${maxAttempts})`);
                await new Promise(resolve => setTimeout(resolve, 1000));
                continue;
              }
            }
            
            // For other errors, don't retry
            break;
          }
          
          if (session) {
            const userData: AuthUser = {
              id: session.user.id,
              email: session.user.email || '',
              name: session.user.user_metadata.full_name || session.user.email?.split('@')[0] || '',
              picture: session.user.user_metadata.avatar_url || DEFAULT_AVATAR_URL,
              access_token: session.access_token,
              user_metadata: {
                full_name: session.user.user_metadata.full_name,
                avatar_url: session.user.user_metadata.avatar_url,
                bio: session.user.user_metadata.bio,
                location: session.user.user_metadata.location,
              }
            };

            // Only update state if user data has actually changed
            setUser(prevUser => {
              if (isUserDataEqual(prevUser, userData)) {
                return prevUser; // Prevent re-render on session refresh
              }
              return userData;
            });
            setIsAuthenticated(true);
            return true;
          } else {
            setUser(null);
            setIsAuthenticated(false);
            return false;
          }
        } catch (networkError) {
          lastError = networkError;
          console.error(`Session refresh attempt ${attempts + 1} failed with network error:`, networkError);
          attempts++;
          
          if (attempts < maxAttempts) {
            console.log(`Retrying session refresh in 1 second... (attempt ${attempts}/${maxAttempts})`);
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
      }
      
      // All attempts failed
      console.error('All session refresh attempts failed. Last error:', lastError);
      
      // Show user-friendly error for critical session failures
      if (lastError && typeof lastError === 'object' && 'message' in lastError) {
        const errorMessage = String(lastError.message);
        if (errorMessage.includes('406') || errorMessage.includes('Not Acceptable')) {
          toast.error('Session expired. Please sign in again.');
        } else if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
          toast.error('Connection lost. Please check your internet and try again.');
        }
      }
      
      setUser(null);
      setIsAuthenticated(false);
      return false;
      
    } catch (error) {
      console.error('Unexpected error in refreshSession:', error);
      
      if (error instanceof Error) {
        if (error.message?.includes('406') || error.message?.includes('Not Acceptable')) {
          console.error('406 error in refreshSession catch - authentication/header issue:', error);
        }
      }
      
      setUser(null);
      setIsAuthenticated(false);
      return false;
    }
  };

  const validateSession = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        console.log('Session validation failed, attempting refresh...');
        return await refreshSession();
      }
      
      // Check if session is expired or about to expire (within 5 minutes)
      const expiresAt = session.expires_at;
      const now = Math.floor(Date.now() / 1000);
      const expiresInSeconds = expiresAt - now;
      
      if (expiresInSeconds < 300) { // Less than 5 minutes
        console.log('Session expires soon, refreshing...');
        return await refreshSession();
      }
      
      return true;
    } catch (error) {
      console.error('Session validation error:', error);
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, isLoading, login, signUp, resendVerificationEmail, signInWithGoogle, signInWithFacebook, logout, getAccessToken, refreshSession, validateSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
